import DOMPurify from 'dompurify';
import { readVideoComments, videoCommentFrames, withVideoCommentFrames, videoCommentAttachments } from './videoComments';
import { messageAttachmentPresentation, type MessageAttachment } from './messageAttachments';

export function hasRichTextContent(html: string | null | undefined): boolean {
  if (!html) return false;
  const container = document.createElement('div');
  container.innerHTML = html;
  if (container.querySelector('img, video, audio, iframe')) return true;
  return (container.textContent ?? '').replace(/\u00a0/g, ' ').trim().length > 0;
}

export function stripInlineAttachments(html: string, ids: number[]): string {
  if (!html || !ids.length) return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  let changed = false;
  ids.forEach((id) => {
    container.querySelectorAll(`[data-attachment-id="${id}"]`).forEach((media) => {
      const link = media.parentElement;
      changed = true;
      if (link instanceof HTMLAnchorElement && link.childNodes.length === 1) link.remove();
      else media.remove();
    });
  });
  container.querySelectorAll<HTMLVideoElement>('video[data-video-comments]').forEach(video => {
    const current = readVideoComments(video.dataset.videoComments);
    const kept = current.flatMap(item => {
      const frames = videoCommentFrames(item), remaining = frames.filter(frame => !ids.includes(frame.attachmentId));
      if (!remaining.length) return [];
      const voiceRemoved = item.voiceAttachmentId !== undefined && ids.includes(item.voiceAttachmentId);
      const comment = voiceRemoved ? { ...item, voiceAttachmentId: undefined, voiceUrl: undefined } : item;
      return [remaining.length === frames.length ? comment : withVideoCommentFrames(comment, remaining)];
    });
    if (JSON.stringify(current) !== JSON.stringify(kept)) { video.dataset.videoComments = JSON.stringify(kept); changed = true; }
  });
  return changed ? container.innerHTML : html;
}

/** Every visible media file and every nested video-frame/voice attachment. */
export function referencedInlineAttachmentIds(html: string): number[] {
  const container = document.createElement('div');
  container.innerHTML = DOMPurify.sanitize(html);
  return [...new Set([
    ...[...container.querySelectorAll<HTMLElement>('img[data-attachment-id], video[data-attachment-id], audio[data-attachment-id]')]
      .map(media => Number(media.dataset.attachmentId)).filter(id => Number.isSafeInteger(id) && id > 0),
    ...videoCommentAttachments(html),
  ])];
}

export function renderRichText(html: string | null | undefined, files?: readonly MessageAttachment[]): string {
  const clean = files?.length ? messageAttachmentPresentation(html, files).html : DOMPurify.sanitize(html ?? '');
  const container = document.createElement('div');
  container.innerHTML = clean;

  container.querySelectorAll('img').forEach((img) => {
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
  });
  container.querySelectorAll('video').forEach((video) => {
    // A historical failed upload may have left a local/empty placeholder.
    if (!video.getAttribute('src') || video.hasAttribute('data-pending') || video.getAttribute('src')?.startsWith('blob:')) { video.remove(); return; }
    video.setAttribute('controls', '');
    video.setAttribute('preload', 'metadata');
    if (video.src) {
      const comments = readVideoComments(video.dataset.videoComments);
      video.dataset.videoComments = JSON.stringify(comments);
      if (comments.length) { video.poster = comments[0].imageUrl; video.style.maxHeight = '360px'; video.style.width = '100%'; video.style.objectFit = 'contain'; video.removeAttribute('controls'); video.dataset.videoReview = 'true'; video.setAttribute('title', `Открыть видео · отметок: ${comments.length}`); }
      const link = document.createElement('a'); link.href = video.src; link.dataset.videoReview = 'true';
      link.dataset.videoComments = JSON.stringify(comments);
      if (video.dataset.attachmentId) link.dataset.attachmentId = video.dataset.attachmentId;
      link.dataset.videoName = video.dataset.filename || 'Видео'; link.className = 'video-review-inline-link';
      link.textContent = comments.length ? `▶ Открыть видео · отметок: ${comments.length}` : 'Открыть видео · правки по кадрам'; video.after(link);
    }
  });
  container.querySelectorAll('a').forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

  const pattern = /(\r\n|\r|\n)|(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi;
  textNodes.forEach((node) => {
    if (node.parentElement?.closest('a, pre, code, script, style')) return;
    const text = node.nodeValue ?? '';
    if (!pattern.test(text)) return;
    pattern.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let offset = 0;
    for (const match of text.matchAll(pattern)) {
      const index = match.index;
      fragment.append(document.createTextNode(text.slice(offset, index)));
      if (match[1]) {
        fragment.append(document.createElement('br'));
      } else {
        const link = document.createElement('a');
        link.href = match[2];
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = match[2];
        fragment.append(link);
      }
      offset = index + match[0].length;
    }
    fragment.append(document.createTextNode(text.slice(offset)));
    node.replaceWith(fragment);
  });

  return DOMPurify.sanitize(container.innerHTML, { ADD_ATTR: ['target', 'loading', 'decoding', 'controls', 'preload'] });
}
